// LandingPage.js - Unified marketing + login/register entry point ("/").
// Rebuilt to match Figma's Voyager Pro LoginPage design: a single page with
// Sign In / Get Started tabs, rather than the old gradient hero + "pick an
// agency, then get redirected to a separate login page" flow. The dedicated
// per-agency pages (/:agencyCode/login, /independent/login, /admin/login)
// still exist unchanged as direct/bookmarked entry points - this page is an
// additional, more discoverable front door that happens to reuse the same
// login API and 2FA flow they use.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import SecurityLockoutMessage from './Auth/SecurityLockoutMessage';
import AuthShell, { AuthField, AuthErrorBanner, AuthPrimaryButton, AuthPasswordInput, AUTH_INPUT } from './Auth/AuthShell';

// Real, existing agencies (matches the old LandingPage.js's list and the
// per-agency routes already built - /mst, /yeti, /bwk).
const AGENCIES = [
  { code: 'MST', name: 'MainStreet Travel Agency' },
  { code: 'YETI', name: 'Yeti Travel Co' },
  { code: 'BWK', name: 'Boardwalk Travel' },
];

// ─── Sign In tab ────────────────────────────────────────────────────────────

function LoginTab() {
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', agency: '' });
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 2FA state (mirrors AgencyLoginPage.js / IndependentLoginPage.js)
  const [show2FA, setShow2FA] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [userContact, setUserContact] = useState({ email: '', phone: '' });
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleLoginSuccess = async (data) => {
    if (!data.token) throw new Error('No authentication token received');
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    await checkAuth();

    setTimeout(() => {
      const currentUser = user || data.user;
      if (currentUser.role === 'agent' && currentUser.status === 'commission_only') {
        navigate('/agent/commissions-only');
        return;
      }
      if (currentUser.role === 'agency_admin') navigate('/agency/dashboard');
      else if (currentUser.role === 'super_admin') navigate('/admin/dashboard');
      else navigate('/agent/dashboard'); // agent + independent_agent both land here
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agency) { setError('Please select your agency to continue.'); return; }
    setError('');
    setLoading(true);

    try {
      const isIndependent = formData.agency === 'independent';
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
        ...(isIndependent ? { independentAgent: true } : { agencyCode: formData.agency }),
      });

      if (response.data.data.requiresTwoFactor) {
        setTempToken(response.data.data.tempToken);
        setUserContact({
          email: response.data.data.maskedEmail || formData.email,
          phone: response.data.data.maskedPhone || '',
        });
        setShow2FA(true);
        setLoading(false);
      } else {
        await handleLoginSuccess(response.data.data);
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.type === 'account_locked' || errorData?.type === 'ip_rate_limited') {
        setError(errorData);
      } else if (errorData?.remainingAttempts !== undefined) {
        setError({ ...errorData, type: 'failed_attempts_warning' });
      } else {
        setError(errorData?.message || 'Invalid credentials');
      }
      setLoading(false);
    }
  };

  const send2FACode = async (selectedMethod = verificationMethod) => {
    setSendingCode(true);
    setError('');
    try {
      await authService.send2FACode({ tempToken, method: selectedMethod });
      setCodeSent(true);
      startResendTimer();
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const resend2FA = async () => {
    if (resendTimer > 0 || sendingCode) return;
    try { await send2FACode(); setError(''); }
    catch { setError('Failed to resend code. Please try again.'); }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authService.verify2FA({ tempToken, code: verificationCode });
      const userData = response.data.user;
      localStorage.removeItem('user');
      localStorage.setItem('token', response.data.token || localStorage.getItem('token'));
      localStorage.setItem('user', JSON.stringify(userData));
      await checkAuth();

      setTimeout(() => {
        if (userData.role === 'agent' && userData.status === 'commission_only') {
          navigate('/agent/commissions-only', { replace: true });
          return;
        }
        const targetPath = userData.role === 'agency_admin' ? '/agency/dashboard'
          : userData.role === 'super_admin' ? '/admin/dashboard'
          : '/agent/dashboard';
        navigate(targetPath, { replace: true });
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div style={{ animation: 'authFadeUp 0.2s ease-out' }}>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
            Two-Factor Authentication
          </h2>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            {!codeSent ? 'Select a delivery method below to send your verification code:'
              : sendingCode ? 'Sending verification code...'
              : `Verification code sent to your ${verificationMethod === 'email' ? 'email' : 'phone'}`}
          </p>
        </div>

        <form onSubmit={handleVerify2FA} className="space-y-4">
          <AuthErrorBanner>{typeof error === 'string' ? error : null}</AuthErrorBanner>

          {!codeSent ? (
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)]">Choose your preferred delivery method</p>
              <div className="flex gap-3">
                <button type="button" onClick={async () => { setVerificationMethod('email'); await send2FACode('email'); }}
                  disabled={sendingCode}
                  className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold border transition-all disabled:opacity-50"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                  {sendingCode && verificationMethod === 'email' ? 'Sending...' : 'Send to Email'}
                </button>
                {userContact.phone && (
                  <button type="button" onClick={async () => { setVerificationMethod('sms'); await send2FACode('sms'); }}
                    disabled={sendingCode}
                    className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold border transition-all disabled:opacity-50"
                    style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                    {sendingCode && verificationMethod === 'sms' ? 'Sending...' : 'Send to Phone'}
                  </button>
                )}
              </div>
              <div className="text-xs text-[var(--muted-foreground)] space-y-0.5">
                <p>Email: {userContact.email}</p>
                {userContact.phone && <p>Phone: {userContact.phone}</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-medium"
              style={{ backgroundColor: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" className="flex-shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
              <span className="text-emerald-700">Code sent successfully! Enter the 6-digit code below to continue.</span>
            </div>
          )}

          <AuthField label="Verification Code" htmlFor="code">
            <input id="code" name="code" type="text" autoComplete="one-time-code" required maxLength="6"
              placeholder="000000" className={`${AUTH_INPUT} text-center text-xl tracking-widest`}
              value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))} />
          </AuthField>

          <div className="text-center">
            <button type="button" onClick={resend2FA} disabled={resendTimer > 0 || sendingCode}
              className="text-xs font-semibold transition-colors disabled:text-[var(--muted-foreground)] disabled:cursor-not-allowed"
              style={{ color: resendTimer > 0 || sendingCode ? undefined : 'var(--accent)' }}>
              {sendingCode ? 'Sending...' : resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend verification code'}
            </button>
          </div>

          <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Verifying…" disabled={verificationCode.length !== 6}>
            Verify Code
          </AuthPrimaryButton>

          <div className="text-center">
            <button type="button" onClick={() => {
              setShow2FA(false); setVerificationCode(''); setError(''); setCodeSent(false);
              setTempToken(''); setSendingCode(false); setLoading(false);
            }} className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors">
              ← Back to login
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={{ animation: 'authFadeUp 0.2s ease-out' }}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
          Welcome Back
        </h2>
        <p className="text-sm text-[var(--muted-foreground)] mt-1">Sign in to your Voyager Pro account</p>
      </div>

      {error && typeof error === 'object' && error.type ? (
        <SecurityLockoutMessage error={error} onRetry={() => { setError(''); setLoading(false); }}
          onForgotPassword={() => navigate('/agent/forgot-password')} />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <AuthField label="Email Address" htmlFor="email">
            <input id="email" name="email" type="email" autoComplete="email" required
              placeholder="you@agency.com" className={AUTH_INPUT}
              value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </AuthField>

          <AuthField label="Password" htmlFor="password"
            hint={
              <button type="button" onClick={() => navigate('/agent/forgot-password')}
                className="text-xs font-semibold hover:underline transition-colors" style={{ color: 'var(--accent)' }}>
                Forgot password?
              </button>
            }>
            <AuthPasswordInput id="password" name="password" autoComplete="current-password" required
              placeholder="••••••••" value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              showPassword={showPassword} setShowPassword={setShowPassword} />
          </AuthField>

          <AuthField label="Agency" htmlFor="agency">
            <select id="agency" name="agency" required className={`${AUTH_INPUT} cursor-pointer`}
              value={formData.agency} onChange={(e) => setFormData({ ...formData, agency: e.target.value })}>
              <option value="">— Select your agency —</option>
              <optgroup label="Independent">
                <option value="independent">Independent Agent</option>
              </optgroup>
              <optgroup label="Partner Agencies">
                {AGENCIES.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
              </optgroup>
            </select>
          </AuthField>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <button type="button" onClick={() => setRemember(v => !v)}
              style={{ height: '22px', width: '40px', borderRadius: '9999px', position: 'relative', flexShrink: 0, backgroundColor: remember ? 'var(--primary)' : 'var(--muted)', transition: 'background-color .15s' }}>
              <span style={{ position: 'absolute', top: '2px', left: remember ? '20px' : '2px', width: '18px', height: '18px', borderRadius: '9999px', backgroundColor: 'white', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
            </button>
            <span className="text-xs text-[var(--muted-foreground)]">Keep me signed in for 30 days</span>
          </label>

          <AuthErrorBanner>{typeof error === 'string' ? error : null}</AuthErrorBanner>

          <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Signing in…">
            Sign In
          </AuthPrimaryButton>
        </form>
      )}

      <div className="text-center mt-6">
        <a href="/admin/login" className="text-xs font-semibold hover:underline transition-colors" style={{ color: 'var(--accent)' }}>
          Agency Admin Login →
        </a>
      </div>
    </div>
  );
}

// ─── Get Started tab ────────────────────────────────────────────────────────

function JoinTab() {
  const navigate = useNavigate();
  const [agencyType, setAgencyType] = useState('independent');
  const [partnerAgency, setPartnerAgency] = useState('');

  const selected = AGENCIES.find(a => a.code === partnerAgency);

  return (
    <div style={{ animation: 'authFadeUp 0.2s ease-out' }}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
          Get Started
        </h2>
        <p className="text-sm text-[var(--muted-foreground)] mt-1">Apply to start selling travel with Voyager Pro</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-2">I am a…</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'independent', label: 'Independent Agent', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg> },
              { id: 'partner', label: 'Partner Agency Agent', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg> },
            ].map(opt => (
              <button key={opt.id} type="button" onClick={() => setAgencyType(opt.id)}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-center transition-all ${
                  agencyType === opt.id ? 'border-2' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]/40'
                }`}
                style={agencyType === opt.id ? { borderColor: 'var(--accent)', backgroundColor: 'rgba(196,135,31,0.06)', color: 'var(--foreground)' } : {}}>
                <span style={{ color: agencyType === opt.id ? 'var(--accent)' : undefined }}>{opt.icon}</span>
                <span className="text-[11px] font-semibold leading-tight">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {agencyType === 'partner' && (
          <AuthField label="Partner Agency" htmlFor="partnerAgency">
            <select id="partnerAgency" className={`${AUTH_INPUT} cursor-pointer`}
              value={partnerAgency} onChange={(e) => setPartnerAgency(e.target.value)}>
              <option value="">— Select partner agency —</option>
              {AGENCIES.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
            </select>
          </AuthField>
        )}

        <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">
          {agencyType === 'independent'
            ? 'Sign up for your own Voyager Pro account and start building quotes right away.'
            : selected
              ? `You'll be taken to ${selected.name}'s application form to apply as one of their agents.`
              : 'Choose which partner agency you\'d like to apply to.'}
        </p>

        {agencyType === 'independent' ? (
          <AuthPrimaryButton type="button" onClick={() => navigate('/register/independent')}>
            Get Started as an Independent Agent
          </AuthPrimaryButton>
        ) : (
          <AuthPrimaryButton type="button" disabled={!selected}
            onClick={() => selected && navigate(`/${selected.code.toLowerCase()}/register`)}>
            {selected ? `Apply to Join ${selected.name}` : 'Select a partner agency above'}
          </AuthPrimaryButton>
        )}
      </div>

      <div className="relative flex items-center gap-3 py-1 mt-6">
        <div className="flex-1 h-px bg-[var(--border)]" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">or</span>
        <div className="flex-1 h-px bg-[var(--border)]" />
      </div>

      <button type="button" onClick={() => navigate('/join-as-agency')}
        className="w-full mt-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)] transition-all">
        My agency wants to join Voyager Pro
      </button>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

const LandingPage = () => {
  const [tab, setTab] = useState('login');

  return (
    <AuthShell
      footer={
        <>
          <p className="text-[11px] text-[var(--muted-foreground)]">© {new Date().getFullYear()} Voyager Pro · Northridge Ventures, LLC</p>
          <div className="flex items-center justify-center gap-3 mt-1">
            <a href="/terms-of-service" className="text-[11px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline transition-colors">Terms of Service</a>
            <span className="text-[var(--border)] text-xs">|</span>
            <a href="/privacy-policy" className="text-[11px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline transition-colors">Privacy Policy</a>
          </div>
        </>
      }
    >
      <div className="flex p-1 bg-white border border-[var(--border)] rounded-xl mb-8 shadow-sm">
        {[
          { id: 'login', label: 'Sign In' },
          { id: 'join', label: 'Get Started' },
        ].map(t => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${
              tab === t.id ? 'text-white shadow-sm' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
            style={tab === t.id ? { backgroundColor: 'var(--primary)' } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'login' ? <LoginTab /> : <JoinTab />}
    </AuthShell>
  );
};

export default LandingPage;
